import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MdOutlineDashboardCustomize,
  MdOutlineAccountBox,
  MdSystemUpdate,
  MdAdminPanelSettings,
  MdOutlineQuiz,
  MdOutlinePeopleAlt,
  MdOutlineSchool,
  MdOutlineSettings,
  MdOutlineLibraryBooks,
  MdOutlineShield,
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
  MdOutlineLogout,
  MdChevronLeft,
  MdChevronRight,
  MdAccountTree,
  MdOutlinePayments,
  MdOutlineInsights,
  MdOutlineBarChart,
  MdHowToReg,
  MdOutlineLock,
  MdCheck,
} from "react-icons/md";
import axios from 'axios';
import { IoMdArrowBack } from "react-icons/io";
import { MdMenu } from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext'
import { useAppContext } from '../appprovider';
import styled from 'styled-components';
import { LocalHospital } from '@mui/icons-material';
import { isSidebarLinkActive } from './sidebarActiveRoute';
import { Btn } from './ui/primitives';
import {
  exitImpersonationView,
  markImpersonationEnter,
  navigateToImpersonationHome,
  buildUserDisplayName,
  getPortalDisplayName,
  getRootAccountDisplayName,
  getRootAccountEmail,
  clearImpersonationStorage,
  stashAdminUsername,
  beginImpersonationTransition,
  clearImpersonationTransition,
  waitForImpersonationPreload,
  isImpersonating,
  clearIdentityEntitlementCache,
} from './impersonationView';
import {
  canAccessAdmin,
  canAccessBilling,
  canAccessSystem,
  canAccessTrainingCompliance,
  canImpersonateGlobally,
  canImpersonateOrgMembers,
  canUseImpersonationSearch,
  isImpersonationSearchLocked,
  canUseGlobalUserSearch,
} from './services/instructorPortalAccess';
import { searchOrgMembers } from './services/orgMemberSearchApi';
import { isFeatureHidden, isFeatureDisabled } from './services/demoPolicy';
import DemoLockedDialog from './ui/DemoLockedDialog';

/**
 * Maps a sidebar item `title` to its demo feature key (see backend DemoFeatureCatalog).
 * Used to hide nav items/sections for read-only demo accounts.
 */
const NAV_FEATURE_KEY = {
  Dashboard: 'nav.dashboard',
  Course: 'nav.course',
  Training: 'nav.training',
  Assessments: 'nav.assessments',
  Enrollments: 'nav.enrollments',
  Scores: 'nav.scores',
  Register: 'nav.register',
  Registration: 'nav.registration',
  Settings: 'nav.settings',
  Admin: 'nav.admin',
  Billing: 'nav.billing',
  System: 'nav.system',
  Workflow: 'nav.workflow',
  'Training & Compliance': 'nav.trainingCompliance',
};

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const PopupBox = styled.div`
  background: white;
  padding: 30px;
  border-radius: 10px;
  width: 400px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const PopupMessage = styled.p`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
`;

const PopupButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const PopupButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  background-color: ${props => props.variant === 'confirm' ? '#4CAF50' : '#bbb'};
  color: white;

  &:hover {
    opacity: 0.9;
  }
`;

const SidebarShell = styled.div`

  .menu {
    position: relative;
    inset: auto;
    z-index: 1200;
    width: 100%;
    height: 100vh;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: ${({ theme }) => theme.railBg};
    color: ${({ theme }) => theme.railText};
    border-right: 1px solid ${({ theme }) => theme.railBorder};
    transition: transform 0.24s ease;
    font-family: ${({ theme }) => theme.fontBody};
  }

  .logo1 {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 76px;
    padding: 20px 18px 18px;
  }

  .logo1 img {
    width: 38px !important;
    height: 38px !important;
    border-radius: 11px !important;
    object-fit: cover;
    box-shadow: 0 4px 14px -4px color-mix(in oklch, ${({ theme }) => theme.accent} 60%, transparent) !important;
  }

  .logo1 .rail__logo {
    width: 38px;
    height: 38px;
    border-radius: 11px;
    flex: none;
    display: grid;
    place-items: center;
    background: linear-gradient(150deg, ${({ theme }) => theme.accent}, ${({ theme }) => theme.accentStrong});
    color: ${({ theme }) => theme.onAccent};
    font-family: ${({ theme }) => theme.fontHead};
    font-weight: 800;
    font-size: 16px;
    box-shadow: 0 4px 14px -4px color-mix(in oklch, ${({ theme }) => theme.accent} 60%, transparent);
  }

  .rail__wordmark {
    display: flex;
    flex-direction: column;
    line-height: 1.05;
    overflow: hidden;
    white-space: nowrap;
    min-width: 0;
  }

  .rail__name {
    font-family: ${({ theme }) => theme.fontHead};
    font-weight: 750;
    font-size: 16px;
    color: ${({ theme }) => theme.railTextStrong};
    letter-spacing: -.01em;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rail__org {
    font-size: 10.5px;
    color: ${({ theme }) => theme.railText};
    margin-top: 2px;
    letter-spacing: .01em;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ---- workspace switcher ----------------------------------------------
     Trigger = the whole workspace block; the menu is a custom panel so the
     list matches the product instead of the OS-rendered <select> popup. */
  .rail__org-switcher {
    position: relative;
    overflow: visible;
  }

  .rail__org-trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 6px 6px 6px 8px;
    margin: -6px 0 -6px -8px;
    border: 1px solid transparent;
    border-radius: ${({ theme }) => theme.rMd};
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background 150ms ease, border-color 150ms ease;
  }

  .rail__org-trigger:hover,
  .rail__org-trigger.is-open {
    background: ${({ theme }) => theme.railHover};
    border-color: ${({ theme }) => theme.railBorder};
  }

  .rail__org-trigger:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
  }

  .rail__org-trigger-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.05;
  }

  .rail__org-caret {
    flex: none;
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.railText};
    transition: transform 180ms ease, color 150ms ease;
  }

  .rail__org-trigger:hover .rail__org-caret { color: ${({ theme }) => theme.railTextStrong}; }
  .rail__org-trigger.is-open .rail__org-caret { transform: rotate(180deg); color: ${({ theme }) => theme.railTextStrong}; }

  .rail__org-menu {
    position: absolute;
    top: calc(100% + 10px);
    left: -4px;
    right: -4px;
    z-index: 60;
    padding: 6px;
    border-radius: ${({ theme }) => theme.rLg};
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    box-shadow: ${({ theme }) => theme.shadowLg};
    animation: railOrgPop 140ms cubic-bezier(.2,.7,.3,1);
  }

  @keyframes railOrgPop {
    from { opacity: 0; transform: translateY(-6px); }
  }

  .rail__org-menu-label {
    padding: 6px 10px 8px;
    font-size: ${({ theme }) => theme.fsMicro};
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textSubtle};
  }

  .rail__org-option {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-radius: ${({ theme }) => theme.rMd};
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background 120ms ease;
  }

  .rail__org-option:hover { background: ${({ theme }) => theme.surface2}; }
  .rail__org-option.is-active { background: ${({ theme }) => theme.accentSoft}; }

  .rail__org-avatar {
    flex: none;
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border-radius: 9px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .02em;
    color: ${({ theme }) => theme.accentStrong};
    background: ${({ theme }) => theme.accentSoft};
    box-shadow: inset 0 0 0 1px color-mix(in oklch, ${({ theme }) => theme.accent} 22%, transparent);
  }

  .rail__org-option.is-active .rail__org-avatar {
    color: #fff;
    background: ${({ theme }) => theme.accent};
  }

  .rail__org-option-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  }

  .rail__org-option-name {
    font-size: 13px;
    font-weight: 650;
    color: ${({ theme }) => theme.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rail__org-option-role {
    margin-top: 1px;
    font-size: ${({ theme }) => theme.fsMicro};
    color: ${({ theme }) => theme.textMuted};
  }

  .rail__org-check {
    flex: none;
    width: 17px;
    height: 17px;
    color: ${({ theme }) => theme.accentStrong};
  }

  .search-bar-container {
    position: relative;
    margin: 0 14px 6px;
    padding: 0;
    border: 1px solid transparent;
    border-radius: ${({ theme }) => theme.rMd};
    background: transparent;
    transition: border-color 150ms ease, background 150ms ease;
  }

  .search-bar-container.is-impersonating {
    padding: 0;
    border-color: transparent;
    background: transparent;
  }

  .search-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    min-height: 38px;
    padding: 0 6px 0 12px;
    border: 1px solid ${({ theme }) => theme.railBorder};
    border-radius: ${({ theme }) => theme.rMd};
    background: ${({ theme }) => theme.railHover};
  }

  .search-bar-container.is-impersonating .search-bar {
    border-color: color-mix(in oklch, ${({ theme }) => theme.accent} 55%, ${({ theme }) => theme.railBorder});
    background: color-mix(in oklch, ${({ theme }) => theme.accent} 16%, ${({ theme }) => theme.railHover});
    box-shadow: none;
  }

  .impersonation-status {
    height: 32px;
    min-height: 32px;
    padding: 0 4px 0 10px;
    gap: 6px;
  }

  .impersonation-status__inline {
    flex: 1;
    min-width: 0;
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme }) => theme.railTextStrong};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .impersonation-status__inline strong {
    font-weight: 700;
    color: ${({ theme }) => theme.accent};
  }

  .search-bar .impersonation-exit-btn {
    flex: none;
    background: ${({ theme }) => theme.railBg};
    border-color: ${({ theme }) => theme.railBorder};
    color: ${({ theme }) => theme.railTextStrong};
    box-shadow: none;
  }

  .search-bar .impersonation-exit-btn:hover:not(:disabled) {
    background: ${({ theme }) => theme.railHover};
    border-color: color-mix(in oklch, ${({ theme }) => theme.accent} 55%, ${({ theme }) => theme.railBorder});
    box-shadow: none;
  }

  .search-bar .impersonation-exit-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .search-bar input {
    min-width: 0;
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    color: ${({ theme }) => theme.railTextStrong};
    font-size: 13px;
    font-weight: 500;
  }

  .search-bar input::placeholder {
    color: ${({ theme }) => theme.railText};
  }

  .search-results {
    position: absolute;
    top: 42px;
    left: 0;
    right: 0;
    z-index: 5;
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.railBorder};
    border-radius: 12px;
    background: ${({ theme }) => theme.railTextStrong};
    box-shadow: 0 24px 50px -18px rgba(17, 20, 38, 0.4);
  }

  .search-results ul {
    margin: 0;
    padding: 6px;
    list-style: none;
  }

  .search-results li {
    padding: 9px 10px;
    border-radius: 8px;
    color: oklch(0.26 0.02 268);
    cursor: pointer;
    font-size: 12px;
  }

  .search-results li:hover {
    background: #eef0ff;
  }

  .menu--list {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 6px 12px;
  }

  .menu--list:not(.bottom) {
    flex: 1;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.railBorder} transparent;
  }

  .menu--list:not(.bottom)::-webkit-scrollbar {
    width: 6px;
  }

  .menu--list:not(.bottom)::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.railBorder};
    border-radius: 3px;
  }

  .menu--list.bottom {
    flex: none;
    margin-top: auto;
    padding: 12px;
  }

  .item,
  .sub-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 13px;
    height: 42px;
    min-height: 42px;
    padding: 0 12px;
    border: 1px solid transparent;
    border-radius: ${({ theme }) => theme.rMd};
    color: ${({ theme }) => theme.railText};
    font-size: 14px;
    font-weight: 550;
    line-height: 1;
    text-decoration: none;
    cursor: pointer;
    transition: background .15s, color .15s;
  }

  .item:hover,
  .sub-item:hover {
    background: ${({ theme }) => theme.railHover};
    color: ${({ theme }) => theme.railTextStrong};
    border-color: transparent;
  }

  .item.is-active,
  .sub-item.is-active {
    background: color-mix(in oklch, ${({ theme }) => theme.accent} 22%, ${({ theme }) => theme.railBg});
    color: ${({ theme }) => theme.railTextStrong};
    border-color: color-mix(in oklch, ${({ theme }) => theme.accent} 45%, transparent);
  }

  .item.is-active::before,
  .sub-item.is-active::before {
    content: "";
    position: absolute;
    left: -12px;
    top: 9px;
    bottom: 9px;
    width: 3px;
    border-radius: 3px;
    background: ${({ theme }) => theme.accent};
  }

  .item svg {
    width: 19px;
    height: 19px;
    flex: none;
  }

  .nav-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item.is-demo-disabled,
  .sub-item.is-demo-disabled {
    opacity: 0.42;
    cursor: not-allowed;
    color: color-mix(in oklch, ${({ theme }) => theme.railText} 65%, transparent);
  }

  .item.is-demo-disabled:hover,
  .sub-item.is-demo-disabled:hover {
    background: transparent;
    color: color-mix(in oklch, ${({ theme }) => theme.railText} 65%, transparent);
    border-color: transparent;
  }

  .rail-section.is-demo-disabled {
    opacity: 0.42;
    cursor: not-allowed;
  }

  .rail-section {
    padding: 14px 12px 6px;
    color: color-mix(in oklch, ${({ theme }) => theme.railText} 70%, transparent);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.14em;
    line-height: 1;
    text-transform: uppercase;
  }

  .training-tab {
    justify-content: flex-start;
  }

  .arrow {
    margin-left: auto;
    color: ${({ theme }) => theme.railText};
    display: grid;
    place-items: center;
    width: 19px;
    height: 19px;
  }

  .arrow svg {
    width: 18px;
    height: 18px;
  }

  .sub-options {
    display: grid;
    gap: 3px;
    margin: 3px 0 6px 28px;
  }

  .sub-item {
    min-height: 31px;
    font-size: 12px;
    color: #b8bece;
  }

  .menu-toggle,
  .back-icon {
    position: fixed;
    top: 16px;
    left: 16px;
    z-index: 1300;
    width: 40px;
    height: 40px;
    padding: 8px;
    border-radius: 12px;
    background: oklch(1 0 0);
    color: oklch(0.26 0.02 268);
    box-shadow: 0 8px 24px -12px rgba(17, 20, 38, 0.45);
    cursor: pointer;
  }

  /* ---- fixed footer: collapse toggle + user + logout ---- */
  .rail__foot {
    flex: none;
    padding: 12px;
    border-top: 1px solid ${({ theme }) => theme.railBorder};
  }

  .collapse-toggle {
    margin-bottom: 4px;
  }

  .rail__user {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 8px;
    border-radius: ${({ theme }) => theme.rMd};
  }

  .rail__avatar {
    width: 34px;
    height: 34px;
    border-radius: ${({ theme }) => theme.rPill};
    flex: none;
    display: grid;
    place-items: center;
    font-weight: 700;
    font-size: 13px;
    background: linear-gradient(150deg, ${({ theme }) => theme.accent}, ${({ theme }) => theme.accentStrong});
    color: ${({ theme }) => theme.onAccent};
  }

  .rail__user-meta {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    line-height: 1.2;
  }

  .rail__user-name {
    font-size: 13px;
    font-weight: 650;
    color: ${({ theme }) => theme.railTextStrong};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rail__user-mail {
    font-size: 11px;
    color: ${({ theme }) => theme.railText};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rail__logout {
    flex: none;
    width: 30px;
    height: 30px;
    display: grid;
    place-items: center;
    border: none;
    border-radius: 8px;
    background: none;
    color: ${({ theme }) => theme.railText};
    cursor: pointer;
    transition: background .15s, color .15s;
  }
  .rail__logout:hover {
    background: ${({ theme }) => theme.railHover};
    color: ${({ theme }) => theme.railTextStrong};
  }
  .rail__logout svg { width: 18px; height: 18px; }

  /* ---- collapsed (icon-only) state ---- */
  .menu.is-collapsed .logo1 { justify-content: center; padding: 20px 0 18px; }
  .menu.is-collapsed .logo1 h1,
  .menu.is-collapsed .rail__wordmark,
  .menu.is-collapsed .search-bar-container,
  .menu.is-collapsed hr,
  .menu.is-collapsed .nav-label,
  .menu.is-collapsed .arrow,
  .menu.is-collapsed .rail__user-meta,
  .menu.is-collapsed .rail__logout,
  .menu.is-collapsed .collapse-label,
  .menu.is-collapsed .rail-section {
    display: none;
  }
  .menu.is-collapsed .menu--list,
  .menu.is-collapsed .rail__foot { padding-left: 10px; padding-right: 10px; }
  .menu.is-collapsed .item,
  .menu.is-collapsed .sub-item,
  .menu.is-collapsed .collapse-toggle,
  .menu.is-collapsed .rail__user { justify-content: center; padding-left: 0; padding-right: 0; gap: 0; }
  .menu.is-collapsed .item.is-active::before,
  .menu.is-collapsed .sub-item.is-active::before { display: none; }

  @media (max-width: 768px) {
    .menu {
      position: fixed;
      inset: 0 auto 0 0;
      width: min(248px, 84vw);
      transform: translateX(-102%);
      box-shadow: 20px 0 50px -24px rgba(0, 0, 0, 0.62);
    }

    .menu.open {
      transform: translateX(0);
    }

    .logo1 {
      min-height: 68px;
      padding: 14px 16px 12px;
    }

    .item,
    .sub-item {
      height: 40px;
      min-height: 40px;
      font-size: 13.5px;
    }
  }
`;


const Sidebar = ({ sidebarData, handleItemClick, logo, collapsed = false, onToggleCollapse }) => {
  const getDisplayLabel = (title) => {
    if (title === "Course") return "Courses";
    if (title === "Assessment") return "Assessments";
    if (title === "Register") return "Course catalog";
    if (title === "Registration") return "Register";
    return title;
  };

  const getInitials = (name) =>
    String(name || 'U').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || 'U';

  // Workspace switcher menu (custom, so the list matches the app rather than the
  // OS-rendered native <select> popup).
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const orgMenuRef = useRef(null);

  useEffect(() => {
    if (!orgMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (orgMenuRef.current && !orgMenuRef.current.contains(event.target)) {
        setOrgMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOrgMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [orgMenuOpen]);

  const renderIcon = (iconName, title) => {
    if (title === "Course") return <MdOutlineSchool />;
    if (title === "Assessment" || title === "Assessments") return <MdOutlineQuiz />;
    if (title === "Enrollments") return <MdOutlinePeopleAlt />;
    if (title === "Scores") return <MdOutlineBarChart />;
    if (title === "Register") return <MdOutlineLibraryBooks />;
    if (title === "Registration") return <MdHowToReg />;
    if (title === "Settings") return <MdOutlineSettings />;
    if (title === "Billing") return <MdOutlinePayments />;
    if (title === "Analysis") return <MdOutlineInsights />;
    if (title === "Training" || title === "Training & Compliance") return <MdOutlineShield />;
    if (title === "Workflow") return <MdAccountTree />;
    if (title === "Logout") return <MdOutlineLogout />;

    switch (iconName) {
      case 'MdAdminPanelSettings':
        return <MdAdminPanelSettings />;
      case 'MdOutlineDashboardCustomize':
        return <MdOutlineDashboardCustomize />;
      case 'MdOutlineAccountBox':
        return <MdOutlineSettings />;
      case 'MdSystemUpdate':
        return <MdOutlineShield />;
      case 'IoIosAddCircleOutline':
        return <MdOutlineQuiz />;
      case 'SiCoursera':
        return <MdOutlineSchool />;
      case 'MdLogout':
        return <MdOutlineLogout/>;
      default:
        return null;
    }
  };

  const [selectedTitle, setSelectedTitle] = useState(null);
  const [selectedName, setSelectedName] = useState("StudyAK");
  const [selectedLogo, setSelectedLogo] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [userId,setUserId1] = useState(localStorage.getItem("userId"));
  const [loggedInEmail, setLoggedInEmail] = useState("");
  const userId1 = localStorage.getItem("userId1");
  const userType = localStorage.getItem("userType");
  const apiUrl = process.env.REACT_APP_API_URL;
  const [isTrainingOpen, setIsTrainingOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const {
    setUserId,
    setPackageDetails,
    entitlements,
    clearEntitlements,
    orgs = [],
    currentOrg,
    loadOrgs,
    switchOrg,
  } = useAppContext();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth < 768);
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  const handleLogout = async () => {
    try {
      setUserId(null);
      setPackageDetails(null);
      clearEntitlements();
      if (isImpersonating()) {
        clearImpersonationStorage();
      }
      signOut();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const toggleTraining = () => {
    setIsTrainingOpen(!isTrainingOpen);
  };

  useEffect(() => {
    if (userType === '2') {
      const selectedEmail = localStorage.getItem("loggedInEmail");
      if (selectedEmail) {
        setLoggedInEmail(selectedEmail);
      }
    } else if (localStorage.getItem("loggedInEmail")) {
      setLoggedInEmail(localStorage.getItem("loggedInEmail"));
    }
    fetchData();
    loadOrgs?.(userId);
  }, [userId]);

  useEffect(() => {
    if (searchTerm) {
      handleSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/organization/${userId}`, {
            headers: {
                'Content-Type': "application/json",
            "Accept": "*/*",
            "Access-Control-Allow-Origin": "*",
            'Authorization': getBasicAuthHeader(),
            },
          });

      if (response.data) {
  setSelectedName(response.data.orgName);
  if (response.data.orgLogo) {
    setSelectedLogo(`data:image/jpeg;base64,${response.data.orgLogo}`);
  } 
  localStorage.setItem("orgId", response.data.orgId);
} else {
        const userId = localStorage.getItem("userId");
        const imagePath = '/main.jpeg';
        const response = await fetch(imagePath);
        const blob = await response.blob();
        const base64String = await convertImageToBase64AndSplit(blob);
        let logoData = base64String;
        const bodyques = {
          userId: userId,
          orgName: selectedName,
          orgLogo: logoData
        };
        //console.log(logoData)
        try {
          const response = await axios.post(`${apiUrl}/api/organization`, bodyques, {
            headers: {
                'Content-Type': "application/json",
            "Accept": "*/*",
            "Access-Control-Allow-Origin": "*",
            'Authorization': getBasicAuthHeader(),
            },
          });
        } catch (error) {
          console.error("Error:", error);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

 const convertImageToBase64AndSplit = (file, maxWidth = 400, quality = 0.5) => {
  return new Promise((resolve, reject) => {
    if (!file || !(file instanceof Blob)) {
      reject("Invalid file provided.");
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");

        // Resize if needed
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Compress to JPEG format
        const compressedBase64 = canvas.toDataURL("image/jpeg", quality); // quality: 0 to 1
        const base64Data = compressedBase64.split(",")[1];

        resolve(base64Data);
      };

      img.onerror = (err) => reject("Failed to load image.");
      img.src = event.target.result;
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
};


  const handleSearch = async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: '*/*',
        'Access-Control-Allow-Origin': '*',
        Authorization: getBasicAuthHeader(),
      };
      if (canImpersonateOrgMembers(userType, entitlements)) {
        const adminUserId = localStorage.getItem('userId1') || localStorage.getItem('userId');
        const orgId = entitlements?.orgId || localStorage.getItem('orgId');
        if (!adminUserId || !orgId) {
          setSearchResults([]);
          return;
        }
        const members = await searchOrgMembers({
          adminUserId,
          orgId,
          search: searchTerm,
        });
        setSearchResults(members);
        return;
      }
      if (!canUseGlobalUserSearch(userType)) {
        setSearchResults([]);
        return;
      }
      const callerId = localStorage.getItem('userId1') || localStorage.getItem('userId');
      const response = await axios.get(
        `${apiUrl}/api/users?search=${searchTerm}&userId=${callerId}`,
        { headers },
      );
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    }
  };

  const applyImpersonatedUserPlan = async (targetUserId) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);
    try {
      const responseUserPlan = await fetch(`${apiUrl}/api/userplans/${targetUserId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
        signal: controller.signal,
      });
      const responseBody = await responseUserPlan.text();
      if (!responseBody) {
        return;
      }
      let data;
      try {
        data = JSON.parse(responseBody);
      } catch {
        console.warn('Impersonation plan response was not JSON; continuing without plan swap.');
        return;
      }
      if (data?.planId == null) {
        return;
      }
      const packageDetails = localStorage.getItem('packageDetails');
      const price = localStorage.getItem('price');
      localStorage.setItem('packageDetails', data.planId);
      localStorage.setItem('price', data.price);
      localStorage.setItem('packageDetails1', packageDetails);
      localStorage.setItem('price1', price);
      if (!responseUserPlan.ok) {
        console.warn('Impersonation plan fetch returned non-OK status; continuing anyway.');
      }
    } catch (error) {
      console.warn('Could not load impersonated user plan; continuing impersonation anyway.', error);
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const handleUserSelection = async (user) => {
    const displayName = buildUserDisplayName(user);
    beginImpersonationTransition(displayName, user.email);

    try {
      const originalUserId = localStorage.getItem('userId1') || localStorage.getItem('userId');
      if (originalUserId) {
        localStorage.setItem('userId1', originalUserId);
      }

      await Promise.all([
        waitForImpersonationPreload(),
        applyImpersonatedUserPlan(user.userId),
      ]);

      stashAdminUsername();
      localStorage.setItem('userId', user.userId);
      localStorage.setItem('loggedInEmail', user.email);
      localStorage.setItem('username', displayName);
      const email = localStorage.getItem('email');
      localStorage.setItem('email', user.email);
      localStorage.setItem('email1', email);
      setUserId1(user.userId);
      setLoggedInEmail(user.email);
      setSearchTerm('');
      setSearchResults([]);

      clearIdentityEntitlementCache();
      markImpersonationEnter();
      navigateToImpersonationHome();
    } catch (error) {
      console.error('Error in handleUserSelection:', error);
      clearImpersonationTransition();
    }
  };


  const handleSearchReset = () => {
    setUserId1(userId1);
    setLoggedInEmail("");
    setSearchTerm("");
    setSearchResults([]);
    exitImpersonationView();
  };

  const [isOpen, setIsOpen] = useState(false);
  const [lockedDialogOpen, setLockedDialogOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const openLockedDialog = (event) => {
    event?.preventDefault?.();
    setLockedDialogOpen(true);
  };

  // Demo read-only mode: section/item hidden vs disabled vs visible.
  const teachingSectionHidden = isFeatureHidden(entitlements, 'nav.section.teaching');
  const manageSectionHidden = isFeatureHidden(entitlements, 'nav.section.manage');
  const teachingSectionDisabled = isFeatureDisabled(entitlements, 'nav.section.teaching');
  const manageSectionDisabled = isFeatureDisabled(entitlements, 'nav.section.manage');

  const isNavItemHidden = (title) => {
    const key = NAV_FEATURE_KEY[title];
    return Boolean(key) && isFeatureHidden(entitlements, key);
  };

  const isNavItemDisabled = (title) => {
    const key = NAV_FEATURE_KEY[title];
    if (!key) return false;
    if (teachingSectionDisabled && ['Dashboard', 'Course', 'Training', 'Assessments', 'Enrollments', 'Scores', 'Register', 'Registration'].includes(title)) {
      return true;
    }
    if (manageSectionDisabled && ['Settings', 'Admin', 'Billing', 'System', 'Workflow', 'Training & Compliance'].includes(title)) {
      return true;
    }
    return isFeatureDisabled(entitlements, key);
  };

  const renderTeachingNavItem = (item, index) => {
    const disabled = isNavItemDisabled(item.title);
    const activeClass = isSidebarLinkActive(location, item.link) ? 'is-active' : '';
    if (disabled) {
      return (
        <div
          key={index}
          className={`item is-demo-disabled ${activeClass}`}
          onClick={(event) => { openLockedDialog(event); toggleMenu(); }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openLockedDialog(event);
              toggleMenu();
            }
          }}
        >
          {renderIcon(item.icon, item.title)}
          <span className="nav-label">{getDisplayLabel(item.title)}</span>
        </div>
      );
    }
    return (
      <Link
        key={index}
        to={item.link}
        className={`item ${activeClass}`}
        onClick={() => { handleItemClick(item.title); toggleMenu(); }}
      >
        {renderIcon(item.icon, item.title)}
        <span className="nav-label">{getDisplayLabel(item.title)}</span>
      </Link>
    );
  };

  const renderManageNavItem = (item, index) => {
    const disabled = isNavItemDisabled(item.title);
    const activeClass = isSidebarLinkActive(location, item.link) ? 'is-active' : '';

    if (item.title === 'Training & Compliance') {
      const handleTrainingHeaderClick = (event) => {
        if (disabled) {
          openLockedDialog(event);
          toggleMenu();
          return;
        }
        toggleTraining();
      };
      return (
        <div key={index}>
          <div
            className={`item training-tab ${disabled ? 'is-demo-disabled' : ''}`}
            onClick={handleTrainingHeaderClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleTrainingHeaderClick(event);
              }
            }}
          >
            {renderIcon(item.icon, item.title)}
            <span className="nav-label">{getDisplayLabel(item.title)}</span>
            {!disabled && (
              <span className="arrow">{isTrainingOpen ? <MdOutlineKeyboardArrowUp /> : <MdOutlineKeyboardArrowDown />}</span>
            )}
          </div>
          {isTrainingOpen && !disabled && (
            <div className="sub-options">
              {item.options.map((subItem, subIndex) => (
                <Link
                  key={subIndex}
                  to={subItem.link}
                  className={`sub-item ${isSidebarLinkActive(location, subItem.link) ? 'is-active' : ''}`}
                >
                  {subItem.title}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (disabled) {
      return (
        <div
          key={index}
          className={`item is-demo-disabled ${activeClass}`}
          onClick={(event) => { openLockedDialog(event); toggleMenu(); }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openLockedDialog(event);
              toggleMenu();
            }
          }}
        >
          {renderIcon(item.icon, item.title)}
          <span className="nav-label">{getDisplayLabel(item.title)}</span>
        </div>
      );
    }

    return (
      <Link
        key={index}
        to={item.link}
        className={`item ${activeClass}`}
        onClick={() => { handleItemClick(item.title); if (item.title === 'Logout') handleLogout(); toggleMenu(); }}
      >
        {renderIcon(item.icon, item.title)}
        <span className="nav-label">{getDisplayLabel(item.title)}</span>
      </Link>
    );
  };

  const regularItems = sidebarData.filter(item => {
    // Training is available to all user types and all packages.
    if (['Settings','Logout', 'Admin', 'Billing', 'System', 'Profile','Training & Compliance', 'Workflow'].includes(item.title)) return false;
    if (teachingSectionHidden) return false;
    if (isNavItemHidden(item.title)) return false;
    return true;
  });
  const Items = sidebarData.filter(item => ['Workflow', 'Settings','Logout', 'Admin', 'Billing', 'System', 'Profile','Training & Compliance'].includes(item.title));

  const bottomItems = Items.filter(item => {
    // Logout lives in the fixed footer (rail__foot) now, so don't list it here.
    if (item.title === 'Logout') return false;
    if (item.title === 'Billing' && !canAccessBilling(entitlements)) return false;
    if (item.title === 'Admin' && !canAccessAdmin(entitlements)) return false;
    if (item.title === 'System' && !canAccessSystem(userType)) return false;
    // Workflow temporarily hidden from sidebar
    if (item.title === 'Workflow') return false;
    if (item.title === 'Training & Compliance') {
      if (!canAccessTrainingCompliance(userType)) return false;
    }
    // Demo read-only mode: hide the whole Manage section or specific items.
    if (manageSectionHidden) return false;
    if (isNavItemHidden(item.title)) return false;
    return true;
  });

  const showGlobalImpersonationSearch = canImpersonateGlobally(userType);
  const showOrgImpersonationSearch = canImpersonateOrgMembers(userType, entitlements);
  const showImpersonationSearch = canUseImpersonationSearch(userType, entitlements) && !isImpersonating();
  const impersonationLocked = isImpersonationSearchLocked(userType, entitlements);
  const showJumpSearchPlaceholder = !showImpersonationSearch && !impersonationLocked && !isImpersonating();
  const impersonating = isImpersonating();
  const railUserName = impersonating
    ? getRootAccountDisplayName()
    : (getPortalDisplayName() || 'User');
  const railUserEmail = impersonating
    ? getRootAccountEmail()
    : (loggedInEmail || localStorage.getItem('email') || '');

  return (
    <SidebarShell>
      {isMobile && (
  <>
      {isOpen ? (
        <IoMdArrowBack className="back-icon" onClick={toggleMenu}/> 
      ) : (
        <MdMenu  className="menu-toggle" onClick={toggleMenu} />
      )}
      </>
      )}
      <div className={`menu ${isOpen ? 'open' : ''} ${collapsed ? 'is-collapsed' : ''}`}>
        <div className="logo1">
          {selectedLogo
            ? <img src={selectedLogo} alt="Organization Logo" />
            : <div className="rail__logo">{getInitials(selectedName)}</div>}
          {orgs.length > 1 ? (
            <div className="rail__wordmark rail__org-switcher" ref={orgMenuRef}>
              <button
                type="button"
                className={`rail__org-trigger ${orgMenuOpen ? 'is-open' : ''}`}
                aria-haspopup="listbox"
                aria-expanded={orgMenuOpen}
                aria-label="Switch workspace"
                onClick={() => setOrgMenuOpen((open) => !open)}
              >
                <span className="rail__org-trigger-text">
                  <span className="rail__name">
                    {(orgs.find((o) => o.orgId === (currentOrg || localStorage.getItem('orgId'))) || {}).orgName || selectedName}
                  </span>
                  <span className="rail__org">{loggedInEmail || localStorage.getItem('email') || 'Instructor workspace'}</span>
                </span>
                <MdOutlineKeyboardArrowDown className="rail__org-caret" aria-hidden="true" />
              </button>

              {orgMenuOpen && (
                <div className="rail__org-menu" role="listbox" aria-label="Your workspaces">
                  <div className="rail__org-menu-label">Switch workspace</div>
                  {orgs.map((org) => {
                    const isActive = org.orgId === (currentOrg || localStorage.getItem('orgId'));
                    return (
                      <button
                        key={org.orgId}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        className={`rail__org-option ${isActive ? 'is-active' : ''}`}
                        onClick={() => {
                          setOrgMenuOpen(false);
                          if (!isActive) switchOrg?.(userId, org.orgId);
                        }}
                      >
                        <span className="rail__org-avatar">{getInitials(org.orgName)}</span>
                        <span className="rail__org-option-text">
                          <span className="rail__org-option-name">{org.orgName}</span>
                          <span className="rail__org-option-role">{org.isOwner ? 'Owner' : 'Instructor'}</span>
                        </span>
                        {isActive && <MdCheck className="rail__org-check" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="rail__wordmark">
              <span className="rail__name">{selectedName}</span>
              <span className="rail__org">{loggedInEmail || localStorage.getItem('email') || 'Instructor workspace'}</span>
            </div>
          )}
        </div>
        {/* While impersonating, the sidebar "View as / Exit view" strip is hidden —
            the impersonation status + Exit control live in the bottom ImpersonationFrame dock. */}
        {showImpersonationSearch && (
    <div className="search-bar-container">
    <div className="search-bar">
      <input
        type="text"
        placeholder={showOrgImpersonationSearch ? 'Search org members…' : 'Search or jump to…'}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {showGlobalImpersonationSearch ? (
      <Btn
        type="button"
        $variant="ghost"
        $sm
        className="impersonation-exit-btn"
        onClick={handleSearchReset}
        disabled={!searchTerm}
      >
        Exit view
      </Btn>
      ) : null}
    </div>

  {searchTerm && searchResults.length > 0 && (
    <div className="search-results">
      <ul>
        {searchResults.map((user) => (
          <li key={user.userId} onClick={() => handleUserSelection(user)}>
            {user.email}
          </li>
        ))}
      </ul>
    </div>
  )}
  {searchTerm && searchResults.length === 0 && (
    <div className="search-results">
      <ul>
        <li style={{ opacity: 0.65, cursor: 'default' }}>
          {showOrgImpersonationSearch ? 'No org members match.' : 'No users match.'}
        </li>
      </ul>
    </div>
  )}
</div>
        )}
        {impersonationLocked && (
          <div className="search-bar-container">
            <div
              className="search-bar"
              title="Upgrade to the Organization plan to view your org as one of its members."
              style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.7, cursor: 'not-allowed' }}
            >
              <MdOutlineLock aria-hidden style={{ flex: 'none', fontSize: '16px' }} />
              <input
                type="text"
                placeholder="View as — Organization plan"
                disabled
                style={{ cursor: 'not-allowed' }}
              />
            </div>
          </div>
        )}
        {showJumpSearchPlaceholder && (
          <div className="search-bar-container">
            <div className="search-bar">
              <input type="text" placeholder="Search or jump to…" disabled />
            </div>
          </div>
        )}

                <div className="menu--list rail__scroll">
          {regularItems.length > 0 && (
            <div className={`rail-section ${teachingSectionDisabled ? 'is-demo-disabled' : ''}`}>Teaching</div>
          )}
          {regularItems.map((item, index) => renderTeachingNavItem(item, index))}
          {bottomItems.length > 0 && (
            <div className={`rail-section ${manageSectionDisabled ? 'is-demo-disabled' : ''}`}>Manage</div>
          )}
          {bottomItems.map((item, index) => renderManageNavItem(item, index))}
        </div>

                <div className="rail__foot">
                  <div className="item collapse-toggle" onClick={onToggleCollapse} title={collapsed ? 'Expand' : 'Collapse'}>
                    {collapsed ? <MdChevronRight /> : <MdChevronLeft />}
                    <span className="nav-label collapse-label">{collapsed ? 'Expand' : 'Collapse'}</span>
                  </div>
                  <div className="rail__user" title={railUserName}>
                    <div className="rail__avatar">{getInitials(railUserName)}</div>
                    <div className="rail__user-meta">
                      <div className="rail__user-name">{railUserName}</div>
                      <div className="rail__user-mail">{railUserEmail}</div>
                    </div>
                    <button
                      className="rail__logout"
                      onClick={handleLogout}
                      title={impersonating ? 'Sign out of your account' : 'Sign out'}
                    >
                      <MdOutlineLogout />
                    </button>
                  </div>
                </div>
              </div>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
          </SidebarShell>
          );
        };
        
        export default Sidebar;
        
